import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { CashbackRequestService, CashbackRequest } from '../../osm-services/cashback-request.service';
import { UserFavAppService, UserFavApp } from '../../osm-services/user-fav-app.service';

@Component({
  selector: 'app-cashback-request',
  templateUrl: './cashback-request.component.html',
  styleUrls: ['./cashback-request.component.scss']
})
export class CashbackRequestComponent implements OnInit, OnDestroy {
  cashbackForm: FormGroup;
  loading = false;
  submitSuccess = false;
  submitError = '';
  
  // User info
  userId = '';
  userDisplayName = '';
  userPhoneNumber = '';
  
  // Image upload
  selectedFile: File | null = null;
  imagePreview: string | null = null;
  uploadProgress = 0;
  
  // User's existing requests
  myRequests: CashbackRequest[] = [];
  loadingRequests = false;
  
  // User's favorite apps for dropdown
  applications: UserFavApp[] = [];
  loadingApps = false;
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private cashbackRequestService: CashbackRequestService,
    private userFavAppService: UserFavAppService
  ) {
    this.cashbackForm = this.fb.group({
      application: ['', [Validators.required, Validators.maxLength(100)]],
      amount: ['', [Validators.required, Validators.min(0.01), Validators.max(10000)]],
      orderDate: ['', [Validators.required]],
      description: ['', [Validators.maxLength(500)]]
    });
  }

  ngOnInit(): void {
    this.initializeData();
  }

  private async initializeData(): Promise<void> {
    // Load user info first to ensure auth token is available
    await this.loadUserInfo();
    // Then load apps and requests in parallel
    this.loadApplications();
    this.loadMyRequests();
  }

  loadApplications(): void {
    this.loadingApps = true;
    console.log('Loading user favorite apps...');
    
    // Subscribe to the user's favorite apps
    this.userFavAppService.userFavApps$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (apps: UserFavApp[]) => {
          console.log('User favorite apps:', apps);
          // Sort by name
          this.applications = apps.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          this.loadingApps = false;
        },
        error: (error) => {
          console.error('Error loading user favorite apps:', error);
          this.loadingApps = false;
        }
      });
    
    // Trigger loading of favorite apps
    this.userFavAppService.loadUserFavApps();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadUserInfo(): Promise<void> {
    try {
      const user = await this.authService.getUser();
      if (user && user.attributes) {
        this.userId = user.attributes.sub;
        this.userDisplayName = user.attributes.name || user.attributes.email || '';
        this.userPhoneNumber = user.attributes.phone_number || '';
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  }

  loadMyRequests(): void {
    if (!this.userId) {
      // Try to load user first, then load requests
      this.authService.getUser().then(user => {
        if (user && user.attributes) {
          this.userId = user.attributes.sub;
          this.fetchMyRequests();
        }
      });
    } else {
      this.fetchMyRequests();
    }
  }

  private fetchMyRequests(): void {
    this.loadingRequests = true;
    
    this.cashbackRequestService.getMyRequests(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.myRequests = response.requests || [];
          this.loadingRequests = false;
        },
        error: (error) => {
          console.error('Error loading requests:', error);
          this.loadingRequests = false;
        }
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(this.selectedFile.type)) {
        this.submitError = 'Please select a valid image file (JPG, PNG, GIF, WebP)';
        this.selectedFile = null;
        return;
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (this.selectedFile.size > maxSize) {
        this.submitError = 'Image file size must be less than 5MB';
        this.selectedFile = null;
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result as string;
      };
      reader.readAsDataURL(this.selectedFile);
      
      this.submitError = '';
    }
  }

  removeImage(): void {
    this.selectedFile = null;
    this.imagePreview = null;
  }

  async onSubmit(): Promise<void> {
    if (this.cashbackForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    if (!this.userId) {
      this.submitError = 'Please log in to submit a cashback request';
      return;
    }

    this.loading = true;
    this.submitError = '';
    this.submitSuccess = false;

    try {
      const formValue = this.cashbackForm.value;
      
      // Prepare request data
      const requestData: any = {
        uid: this.userId,
        displayName: this.userDisplayName,
        phoneNumber: this.userPhoneNumber,
        application: formValue.application,
        amount: formValue.amount.toString(),
        orderDate: formValue.orderDate,
        description: formValue.description || ''
      };

      // Upload image if selected
      if (this.selectedFile) {
        try {
          const imageUrl = await this.cashbackRequestService.uploadImage(this.selectedFile, this.userId);
          requestData.imageUrl = imageUrl;
          requestData.filePath = imageUrl;
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          // Continue without image if upload fails
        }
      }

      // Submit the request
      this.cashbackRequestService.submitRequest(requestData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.submitSuccess = true;
            this.cashbackForm.reset();
            this.removeImage();
            
            // Reload requests list
            setTimeout(() => {
              this.loadMyRequests();
            }, 1000);
          },
          error: (error) => {
            this.loading = false;
            this.submitError = error.error?.message || 'Failed to submit request. Please try again.';
            console.error('Submit error:', error);
          }
        });

    } catch (error) {
      this.loading = false;
      this.submitError = 'An error occurred. Please try again.';
      console.error('Submit error:', error);
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.cashbackForm.controls).forEach(key => {
      const control = this.cashbackForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.cashbackForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.cashbackForm.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldLabel(fieldName)} is required`;
      if (field.errors['min']) return `${this.getFieldLabel(fieldName)} must be greater than 0`;
      if (field.errors['max']) return `${this.getFieldLabel(fieldName)} is too large`;
      if (field.errors['maxlength']) return `${this.getFieldLabel(fieldName)} is too long`;
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      application: 'Application/Store',
      amount: 'Amount',
      orderDate: 'Order Date',
      description: 'Description'
    };
    return labels[fieldName] || fieldName;
  }

  getStatusBadgeClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'approved': return 'badge-success';
      case 'rejected': return 'badge-danger';
      case 'pending': return 'badge-warning';
      default: return 'badge-secondary';
    }
  }

  viewImage(imageUrl: string): void {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  }

  goBack(): void {
    this.router.navigate(['/']);
  }
}
